import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { queryRetry } from "@/lib/hooks/auth/use-auth-aware-retry";
import { fetchEnhancedTimeline } from "./timeline-fetchers";
import type { TimelineItem, UseEnhancedTimelineOptions } from "./timeline-types";

/**
 * Timeline rows expose `communications.body` / ticket update `content` for search and for
 * `SafeEmailBody` rendering. Values pass through {@link normalizeTimelineStoredBody} so
 * entity-encoded HTML matches the CRM pipeline documented on `prepareEmailBodyMarkupForDomPurify`.
 */

export type { TimelineItem, TimelineFilters, TimelineSortOptions } from "./timeline-types";

export const useEnhancedTimeline = (email: string, options: UseEnhancedTimelineOptions = {}) => {
  const queryKey = useMemo(
    () => [
      "enhancedTimeline",
      email,
      {
        filters: options.filters,
        sort: options.sort,
        limit: options.limit,
        enabled: options.enabled,
        ticketId: options.ticketId,
        contactId: options.contactId,
        // Part of the CACHE IDENTITY, not just a fetch argument: it decides
        // whether the company's shared-line activity is inherited into the
        // result. Left out of the key, a "this person only" view and a full
        // inherited view would silently share one cache entry and show each
        // other's rows.
        excludeCompanyActivity: options.excludeCompanyActivity,
      },
    ],
    [
      email,
      options.filters,
      options.sort,
      options.limit,
      options.enabled,
      options.ticketId,
      options.contactId,
      options.excludeCompanyActivity,
    ]
  );

  const supabaseQuery = useQuery<TimelineItem[], Error>({
    queryKey,
    queryFn: () => fetchEnhancedTimeline(email, options),
    enabled: Boolean(
      options.enabled !== false &&
        (options.ticketId || (!!email && typeof email === "string" && email.trim() !== ""))
    ),
    retry: queryRetry,
    staleTime: 60 * 1000,
  });

  return {
    data: supabaseQuery.data,
    isLoading: supabaseQuery.isLoading,
    error: supabaseQuery.error,
    refetch: supabaseQuery.refetch,
    isRefetching: supabaseQuery.isRefetching,
  };
};
